import { Form, Input, Radio } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateParameter,
  useMutationCreateParameterGroup,
  useMutationUpdateParameter,
  useMutationUpdateParameterGroup,
  useQueryGetParameterGroupLookup,
  useQueryGetUnitBrandLookup,
} from "src/queries/administrationSetup.queries";
import type {
  ParameterGroupPayload,
  ParameterGroupRecord,
  ParameterPayload,
  ParameterRecord,
  PossibleValue,
} from "src/services/administrationSetupService";
import Notification from "src/util/libs/notification";
import ParameterPossibleValuesEditor from "../ParameterPossibleValuesEditor";
import { getLookupItems, isActive } from "../setupUtils";

type ParameterSetupKind = "group" | "parameter";
type ParameterSetupRecord = ParameterGroupRecord | ParameterRecord;

interface ParameterGroupsAddEditProps {
  open: boolean;
  record?: ParameterSetupRecord | null;
  onClose: () => void;
}

interface ParameterGroupFormValues {
  name: string;
  code: string;
  active: boolean;
}

interface ParameterFormValues {
  parameterGroupId?: string | number;
  packetCode: string;
  propertyNameComm: string;
  propertyNameInternal: string;
  description?: string;
  bytes?: string;
  type?: string;
  minValue?: string;
  maxValue?: string;
  multiplier?: string;
  units?: string;
  active: boolean;
  makeId?: string | number;
}

type ParameterSetupFormValues = ParameterGroupFormValues & Partial<ParameterFormValues>;

const getParameterRecord = (record?: ParameterSetupRecord | null) =>
  record as ParameterRecord | null | undefined;

const getSetupKind = (): ParameterSetupKind => "group";

const ParameterGroupsAddEdit = ({ open, record, onClose }: ParameterGroupsAddEditProps) => {
  const kind = getSetupKind();
  const { t } = useTranslation();
  const [form] = Form.useForm<ParameterSetupFormValues>();
  const [possibleValues, setPossibleValues] = useState<PossibleValue[]>([]);
  const isParameter = kind === "parameter";
  const isEditing = Boolean(record);
  const parameterRecord = getParameterRecord(record);
  const createGroupMutation = useMutationCreateParameterGroup();
  const updateGroupMutation = useMutationUpdateParameterGroup();
  const createParameterMutation = useMutationCreateParameter();
  const updateParameterMutation = useMutationUpdateParameter();
  const groupLookupQuery = useQueryGetParameterGroupLookup(open && isParameter);
  const makeLookupQuery = useQueryGetUnitBrandLookup(open && isParameter);

  const groupOptions = useMemo(
    () =>
      getLookupItems(groupLookupQuery.data?.data).map((item) => ({
        value: item.id,
        label: item.name ?? item.code ?? String(item.id),
      })),
    [groupLookupQuery.data?.data]
  );

  const makeOptions = useMemo(
    () =>
      getLookupItems(makeLookupQuery.data?.data).map((item) => ({
        value: item.id,
        label: item.name ?? item.code ?? String(item.id),
      })),
    [makeLookupQuery.data?.data]
  );

  useEffect(() => {
    if (!open) return;

    if (isParameter) {
      form.setFieldsValue({
        parameterGroupId: parameterRecord?.parameterGroup?.id,
        packetCode: parameterRecord?.packetCode ?? "",
        propertyNameComm: parameterRecord?.propertyNameComm ?? "",
        propertyNameInternal: parameterRecord?.propertyNameInternal ?? "",
        description: parameterRecord?.description ?? "",
        bytes: parameterRecord?.bytes ?? "",
        type: parameterRecord?.type ?? "",
        minValue: parameterRecord?.minValue ?? "",
        maxValue: parameterRecord?.maxValue ?? "",
        multiplier: parameterRecord?.multiplier ?? "",
        units: parameterRecord?.units ?? "",
        active: record ? isActive(record.active) : true,
        makeId: parameterRecord?.make?.id,
      });
      setPossibleValues(parameterRecord?.possibleValues ?? []);
      return;
    }

    const groupRecord = record as ParameterGroupRecord | null | undefined;

    form.setFieldsValue({
      name: groupRecord?.name ?? "",
      code: groupRecord?.code ?? "",
      active: record ? isActive(record.active) : true,
    });
    setPossibleValues([]);
  }, [form, isParameter, open, parameterRecord, record]);

  const handleClose = () => {
    form.resetFields();
    setPossibleValues([]);
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    try {
      if (isParameter) {
        const payload: ParameterPayload = {
          parameterGroupId: values.parameterGroupId!,
          packetCode: (values.packetCode ?? "").trim(),
          propertyNameComm: (values.propertyNameComm ?? "").trim(),
          propertyNameInternal: (values.propertyNameInternal ?? "").trim(),
          description: values.description?.trim() ?? "",
          bytes: values.bytes?.trim() ?? "",
          type: values.type?.trim() ?? "",
          minValue: values.minValue?.trim() ?? "",
          maxValue: values.maxValue?.trim() ?? "",
          multiplier: values.multiplier?.trim() ?? "",
          units: values.units?.trim() ?? "",
          active: values.active,
          makeId: values.makeId!,
          createdBy: parameterRecord?.createdBy ?? "",
          updatedBy: parameterRecord?.updatedBy ?? "",
          possibleValues,
        };

        if (record) {
          await updateParameterMutation.mutateAsync({ id: record.id, payload });
        } else {
          await createParameterMutation.mutateAsync(payload);
        }
      } else {
        const payload: ParameterGroupPayload = {
          name: values.name.trim(),
          code: values.code.trim(),
          active: values.active,
        };

        if (record) {
          await updateGroupMutation.mutateAsync({ id: record.id, payload });
        } else {
          await createGroupMutation.mutateAsync(payload);
        }
      }

      Notification.success({
        message: t(
          isParameter
            ? isEditing
              ? "parameter_updated_successfully"
              : "parameter_added_successfully"
            : isEditing
              ? "parameter_group_updated"
              : "parameter_group_added_successfully"
        ),
      });
      handleClose();
    } catch (error: unknown) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;

      if (!responseMessage) {
        Notification.error({
          message: t("error"),
          description: t("setup_save_failed"),
        });
      }
    }
  };

  const loading =
    createGroupMutation.isPending ||
    updateGroupMutation.isPending ||
    createParameterMutation.isPending ||
    updateParameterMutation.isPending;

  return (
    <CustomModal
      open={open}
      title={t(
        isParameter
          ? isEditing
            ? "edit_parameter"
            : "add_parameter"
          : isEditing
            ? "edit_parameter_group"
            : "new_parameter_group"
      )}
      onCancel={handleClose}
      destroyOnHidden
      width={isParameter ? 900 : 560}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 gap-x-4 gap-y-0 pt-4 sm:grid-cols-2 lg:grid-cols-2"
      >
        {!isParameter && (
          <>
            <Form.Item
              name="name"
              label={t("parameter_group_name")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("parameter_group_name_required"),
                },
                { max: 32, message: t("name_max_length", { count: 32 }) },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_parameter_group_name")} maxLength={32} />
            </Form.Item>

            <Form.Item
              name="code"
              label={t("code")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("parameter_group_code_required"),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_code")} />
            </Form.Item>
          </>
        )}

        {isParameter && (
          <>
            <Form.Item
              name="propertyNameComm"
              label={t("name")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("parameter_name_required"),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_name")} />
            </Form.Item>

            <Form.Item
              name="parameterGroupId"
              label={t("group")}
              rules={[{ required: true, message: t("parameter_group_required") }]}
              className="mb-3!"
            >
              <CustomSelect
                options={groupOptions}
                placeholder={t("select_parameter_group")}
                loading={groupLookupQuery.isLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="packetCode"
              label={t("packet_code")}
              rules={[
                { required: true, whitespace: true, message: t("packet_code_required") },
                {
                  // pattern: /^\d{16}$/,
                  max: 16,
                  message: t("packet_code_max_16_digits"),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_packet_code")} />
            </Form.Item>

            <Form.Item name="bytes" label={t("bytes")} className="mb-3!">
              <CustomInput placeholder={t("enter_bytes")} />
            </Form.Item>

            <Form.Item name="type" label={t("type")} className="mb-3!">
              <CustomInput placeholder={t("enter_type")} />
            </Form.Item>

            <Form.Item name="minValue" label={t("min_value")} className="mb-3!">
              <CustomInput placeholder={t("enter_min_value")} />
            </Form.Item>

            <Form.Item name="maxValue" label={t("max_value")} className="mb-3!">
              <CustomInput placeholder={t("enter_max_value")} />
            </Form.Item>

            <Form.Item
              name="multiplier"
              label={t("multiplier")}
              rules={[
                {
                  pattern: /\b\d{1,}(?:[.,]\d+)?\b/,
                  message: t("invalid_number_format"),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_multiplier")} />
            </Form.Item>

            <Form.Item name="units" label={t("unit_of_measure")} className="mb-3!">
              <CustomInput placeholder={t("enter_unit_of_measure")} />
            </Form.Item>

            <Form.Item
              name="propertyNameInternal"
              label={t("property_name_internal")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("property_name_internal_required"),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_property_name_internal")} />
            </Form.Item>

            <Form.Item
              name="makeId"
              label={t("parameter_make")}
              rules={[{ required: true, message: t("make_required") }]}
              className="mb-3!"
            >
              <CustomSelect
                options={makeOptions}
                placeholder={t("select_unit_make")}
                loading={makeLookupQuery.isLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="active" label={t("status")} className="mb-3!">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value>{t("active")}</Radio.Button>
            <Radio.Button value={false}>{t("inactive")}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {isParameter && (
          <>
            <Form.Item
              name="description"
              label={t("description")}
              className="col-span-1 mb-3! sm:col-span-2 lg:col-span-2"
            >
              <Input.TextArea
                rows={4}
                placeholder={t("enter_description")}
                className="px-2 py-4 custom-input no-resize"
              />
            </Form.Item>

            <div className="col-span-1 sm:col-span-2 lg:col-span-2">
              <ParameterPossibleValuesEditor values={possibleValues} onChange={setPossibleValues} />
            </div>
          </>
        )}

        <Form.Item className="col-span-1 mb-0! sm:col-span-2 lg:col-span-2">
          <div className="flex justify-end">
            <CustomActionButtons
              onCancel={handleClose}
              onOk={handleSubmit}
              loading={loading}
              okProps={{ htmlType: "button" }}
              cancelBtnLabel="cancel"
              okBtnLabel={isEditing ? "update" : "add"}
            />
          </div>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default ParameterGroupsAddEdit;
